import { Filter } from '@iot-platform/models/common';
import { get } from 'lodash';
import { additionalFiltersByCriteriaKey, FilterCategoryV2, FilterFieldType, FilterFieldV2 } from '../models';
import { isDatePickerBatchFilter, processDatePickerBatch } from './date-picker-batch.helper';
import { findConflictingFilterIndex, isDuplicateFilter } from './filter-comparison.helper';
import { hasBlankOrNotBlankFilter, isMultiSelectBatchFilter, processMultiSelectBatch } from './multi-select-batch.helper';

export function applyFilter(
  field: FilterFieldV2,
  newFilter: Filter,
  currentFilters: Filter[],
  multipleOperatorSupport: boolean
): { filters: Filter[]; applied: boolean } {
  if (multipleOperatorSupport) {
    return applyFilterWithMultipleOperator(field, newFilter, currentFilters);
  }
  return applyFilterWithSingleOperator(field, newFilter, currentFilters);
}
/**
 * Process and apply a new filter to the existing filters list.
 * Enforces SINGLE OPERATOR per criteriaKey rule:
 * - Only one operator type allowed per criteriaKey at any time
 * - When operator changes, all filters with different operators for that criteriaKey are removed
 * - Multiple values are allowed for the same criteriaKey+operator combination
 *
 * Handles:
 * - Multi-select batch updates (same operator, multiple values)
 * - Operator changes (removes filters with different operators)
 * - Duplicate detection and prevention
 * - Value additions to existing criteriaKey+operator combinations
 *
 * @param field - Field configuration with validation options
 * @param newFilter - Filter to apply (single or batch)
 * @param currentFilters - Current list of applied filters
 * @returns Result object with updated filters array, applied flag, and descriptive reason
 */
export function applyFilterWithSingleOperator(field: FilterFieldV2, newFilter: Filter, currentFilters: Filter[]): { filters: Filter[]; applied: boolean } {
  const result = initializeResult(field);

  // Handle multi-select batch updates
  if (isMultiSelectBatchFilter(newFilter)) {
    const batchFilters = processMultiSelectBatch(newFilter, []);
    return handleBatchFilterWithSingleOperator(field, newFilter, currentFilters, batchFilters, result);
  }

  // Handle date-picker batch updates
  if (isDatePickerBatchFilter(newFilter)) {
    const batchFilters = processDatePickerBatch(newFilter, []);
    return handleBatchFilterWithSingleOperator(field, newFilter, currentFilters, batchFilters, result);
  }

  // Validate filter
  const validationResult = validateFilter(newFilter);
  if (!validationResult.valid) {
    result.filters.push(...currentFilters);
    return result;
  }

  // Handle clear filter case (null or undefined value)
  if (validationResult.isClear) {
    const clearedFilters = currentFilters.filter((f) => f.criteriaKey !== newFilter.criteriaKey);
    result.filters.push(...clearedFilters);
    result.applied = true;
    return result;
  }

  // Separate filters: remove all with same criteriaKey, keep others
  const filtersWithoutThisCriteriaKey = currentFilters.filter((f) => f.criteriaKey !== newFilter.criteriaKey);
  const filtersWithSameKeyAndOperator = currentFilters.filter(
    (f) => f.criteriaKey === newFilter.criteriaKey && f.criteriaOperator === newFilter.criteriaOperator
  );

  // Check for exact duplicate in existing filters with same key+operator
  if (
    isDuplicateFilter(
      newFilter as { criteriaKey: string; criteriaOperator: string; value: unknown },
      [...filtersWithSameKeyAndOperator] as Array<{ criteriaKey: string; criteriaOperator: string; value: unknown }>
    )
  ) {
    result.filters.push(...currentFilters);
    return result;
  }

  // Build updated filters: other keys + same key+operator + new filter
  const updatedFilters = [...filtersWithoutThisCriteriaKey, ...filtersWithSameKeyAndOperator, newFilter];
  result.filters.push(...updatedFilters);
  result.applied = true;

  return result;
}

/**
 * Helper function to handle batch filter processing with single operator rule
 */
function handleBatchFilterWithSingleOperator(
  field: FilterFieldV2,
  newFilter: Filter,
  currentFilters: Filter[],
  batchFilters: Filter[],
  result: { filters: Filter[]; applied: boolean }
): { filters: Filter[]; applied: boolean } {
  // Handle empty batch - clear all filters with this criteriaKey
  if (!batchFilters || batchFilters.length === 0) {
    const clearedFilters = currentFilters.filter((f) => f.criteriaKey !== newFilter.criteriaKey);
    result.filters.push(...clearedFilters);
    result.applied = true;
    return result;
  }

  // Remove all existing filters with same criteriaKey (enforce single operator)
  let workingFilters = currentFilters.filter((f) => f.criteriaKey !== newFilter.criteriaKey);
  if (field.fieldType === FilterFieldType.DATE) {
    // Keep all existing filters and those with same criteriaKey and operator(enforce single operator)
    workingFilters = currentFilters.filter(
      (f) =>
        f.criteriaKey !== newFilter.criteriaKey || (f.criteriaKey === newFilter.criteriaKey && f.criteriaOperator?.includes(`${newFilter.criteriaOperator}`))
    );
  }

  let addedCount = 0;

  // Process each filter from the batch
  for (const batchFilter of batchFilters) {
    // Add unique filter
    workingFilters.push(batchFilter);
    addedCount++;
  }

  result.filters.push(...workingFilters);
  result.applied = addedCount > 0;

  return result;
}

/**
 * Process and apply a new filter to the existing filters list.
 * Supports MULTIPLE OPERATORS per criteriaKey:
 * - Allows different operators for the same criteriaKey simultaneously
 * - For batch filters: only removes filters with same criteriaKey+operator combination
 * - Conflicts resolved when same key+value exists with different operator (replaces old)
 * - Multiple values are allowed for the same criteriaKey+operator combination
 *
 * Handles:
 * - Multi-select batch updates (same operator, multiple values)
 * - Conflict resolution (same key+value, different operator)
 * - Duplicate detection and prevention
 * - Operator coexistence (e.g., "equals" and "not_equals" can both exist)
 *
 * @param field - Field configuration with validation options
 * @param newFilter - Filter to apply (single or batch)
 * @param currentFilters - Current list of applied filters
 * @returns Result object with updated filters array, applied flag, and descriptive reason
 */
export function applyFilterWithMultipleOperator(field: FilterFieldV2, newFilter: Filter, currentFilters: Filter[]): { filters: Filter[]; applied: boolean } {
  const result = initializeResult(field);

  // Handle multi-select batch updates
  if (isMultiSelectBatchFilter(newFilter)) {
    const batchFilters = processMultiSelectBatch(newFilter, []);
    return handleBatchFilterWithMultipleOperator(field, newFilter, currentFilters, batchFilters, result);
  }

  // Handle date-picker batch updates
  if (isDatePickerBatchFilter(newFilter)) {
    const batchFilters = processDatePickerBatch(newFilter, []);
    return handleBatchFilterWithMultipleOperator(field, newFilter, currentFilters, batchFilters, result);
  }

  // Validate filter
  const validationResult = validateFilter(newFilter);
  if (!validationResult.valid) {
    result.filters.push(...currentFilters);
    return result;
  }

  // Handle clear filter case (null or undefined value)
  if (validationResult.isClear) {
    const clearedFilters = currentFilters.filter((f) => !(f.criteriaKey === newFilter.criteriaKey && f.criteriaOperator === newFilter.criteriaOperator));
    result.filters.push(...clearedFilters);
    result.applied = true;
    return result;
  }

  // Check for conflicting filter (same key+value, different operator)
  const conflictingFilterIndex = findConflictingFilterIndex(
    newFilter as { criteriaKey: string; criteriaOperator: string; value: unknown },
    currentFilters as Array<{ criteriaKey: string; criteriaOperator: string; value: unknown }>
  );

  if (conflictingFilterIndex !== -1) {
    // Replace the conflicting filter with the new one
    const conflictResolvedFilters = [...currentFilters];
    conflictResolvedFilters[conflictingFilterIndex] = newFilter;

    result.filters.push(...conflictResolvedFilters);
    result.applied = true;
    return result;
  }

  // Check for exact duplicate
  if (
    isDuplicateFilter(
      newFilter as { criteriaKey: string; criteriaOperator: string; value: unknown },
      currentFilters as Array<{ criteriaKey: string; criteriaOperator: string; value: unknown }>
    )
  ) {
    result.filters.push(...currentFilters);
    return result;
  }

  // Add new filter - it's unique and not conflicting
  const updatedFilters = [...currentFilters, newFilter];
  result.filters.push(...updatedFilters);
  result.applied = true;

  return result;
}

/**
 * Helper function to handle batch filter processing with multiple operator support
 */
function handleBatchFilterWithMultipleOperator(
  field: FilterFieldV2,
  newFilter: Filter,
  currentFilters: Filter[],
  batchFilters: Filter[],
  result: { filters: Filter[]; applied: boolean }
): { filters: Filter[]; applied: boolean } {
  // Handle empty batch - clear only filters with this criteriaKey+operator
  if (!batchFilters || batchFilters.length === 0) {
    const clearedFilters = currentFilters.filter((f) => !(f.criteriaKey === newFilter.criteriaKey && f.criteriaOperator === newFilter.criteriaOperator));
    result.filters.push(...clearedFilters);
    result.applied = true;
    return result;
  }

  // Remove only filters with same criteriaKey+operator combination
  const workingFilters = currentFilters.filter((f) => !(f.criteriaKey === newFilter.criteriaKey && f.criteriaOperator === newFilter.criteriaOperator));

  let addedCount = 0;
  let replacedCount = 0;

  // Process each filter from the batch
  for (const batchFilter of batchFilters) {
    // Check for conflicting filter (same key+value, different operator)
    const conflictingIndex = findConflictingFilterIndex(
      batchFilter as { criteriaKey: string; criteriaOperator: string; value: unknown },
      workingFilters as Array<{ criteriaKey: string; criteriaOperator: string; value: unknown }>
    );

    if (conflictingIndex !== -1) {
      // Replace conflicting filter
      workingFilters[conflictingIndex] = batchFilter;
      replacedCount++;
      continue;
    }

    // Add unique filter
    workingFilters.push(batchFilter);
    addedCount++;
  }

  result.filters.push(...workingFilters);
  result.applied = addedCount > 0 || replacedCount > 0;

  return result;
}

/**
 * Helper function to initialize result object
 */
function initializeResult(_field: FilterFieldV2): { filters: Filter[]; applied: boolean } {
  return {
    filters: [],
    applied: false
  };
}

/**
 * Helper function to validate a filter has required properties and valid value
 */
export function validateFilter(filter: Filter): { valid: boolean; isClear?: boolean } {
  if (!filter.criteriaKey || !filter.criteriaOperator) {
    return { valid: false };
  }

  const isBlankOrNotBlank = hasBlankOrNotBlankFilter(filter);
  const hasEmptyValue = filter.value === null || filter.value === undefined;
  const hasEmptyDisplay = filter.valueDisplay === null || filter.valueDisplay === undefined;

  const isClear = (isBlankOrNotBlank && hasEmptyValue && hasEmptyDisplay) || (!isBlankOrNotBlank && hasEmptyValue);

  return { valid: true, ...(isClear && { isClear: true }) };
}

/**
 * Helper function to add or remove special filters based on filter options in categories.
 *
 * @param categories - Configuration categories and fields
 * @param filters - Current applied filters
 * @returns Fully processed filters with special filters added or removed as needed
 */
export function manageSpecialFiltersFromConfiguration(categories: (FilterFieldV2 | FilterCategoryV2)[], filters: Filter[]): Filter[] {
  const criteriaKeysWithAdditionalFilter = Object.keys(additionalFiltersByCriteriaKey);
  let fullFilters: Filter[] = [...filters];

  for (const criteriaKey of criteriaKeysWithAdditionalFilter) {
    const additionalFilterNames = Object.keys(additionalFiltersByCriteriaKey[criteriaKey]);

    for (const additionalFilterName of additionalFilterNames) {
      const hasMatchingOptionInConfiguration = categories?.some((c) =>
        get(c, 'fields', []).some(
          (f: FilterCategoryV2) =>
            f.key === criteriaKey && Object.entries(get(f, 'options', {}))?.some((entry) => entry[0] === additionalFilterName && entry[1])
        )
      );

      if (hasMatchingOptionInConfiguration) {
        fullFilters = fullFilters.filter((f) => f.criteriaKey !== additionalFilterName);

        const hasSpecialFilter = filters.some((f) => f.criteriaKey === criteriaKey);
        if (hasSpecialFilter) {
          fullFilters.push(additionalFiltersByCriteriaKey[criteriaKey][additionalFilterName]);
        }
      }
    }
  }
  return fullFilters;
}
